import { useMemo } from 'react';
import { useMedigapPlansQuery } from '@coverright/data-access/medigap';
import { Gender, MedigapPlanName } from '@coverright/data-access/types/medigap';
import { getEffectiveDate } from '@coverright/utils';
import type { MedigapLetterRecommendation } from '../plan-engine-ai/plan-engine-types';
import { useMedigapQuote } from './use-medigap-quote';
import { useMedicareQuote } from '../medicare/use-medicare-quote';
import { useUserProfile } from '../enrollment/use-user-profile';

export type CarrierNameMap = Record<number, { company_name: string; parent_company: string | null }>;

/**
 * Queries the GraphQL medigap API and returns a NAIC→{company_name, parent_company} map.
 * The plan engine API returns incorrect company names; this hook fetches the canonical
 * values needed for logo lookup via getMedigapCompanyLogo().
 */
export function useMedigapCarrierLookup(plans: MedigapLetterRecommendation[]): CarrierNameMap {
  const profile = useUserProfile();
  const quote = useMedicareQuote();
  const medigapQuote = useMedigapQuote();

  const planLetters = useMemo(
    () => plans.map(p => p.plan_letter as MedigapPlanName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plans.map(p => p.plan_letter).join(',')],
  );

  const zip = medigapQuote?.medigapFilterState?.zip || quote?.zip || '';
  const county = medigapQuote?.medigapFilterState?.county || quote?.county || '';

  const filter = useMemo(
    () => ({
      age: medigapQuote?.medigapFilterState?.age || profile?.age || 64,
      companies: [],
      county,
      effectiveDate: getEffectiveDate(medigapQuote?.medigapFilterState?.effectiveDate, profile?.birthDate),
      gender: (medigapQuote?.medigapFilterState?.gender || profile?.gender || Gender.F) as Gender,
      monthlyPlanPremiumRanges: [],
      tobacco: medigapQuote?.medigapFilterState?.tobacco || false,
      zip,
      planNames: planLetters,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      medigapQuote?.medigapFilterState,
      profile?.age,
      profile?.gender,
      profile?.birthDate,
      county,
      zip,
      planLetters,
    ],
  );

  const { data } = useMedigapPlansQuery({
    variables: { page: { page: 0, size: 200 }, filter },
    skip: !planLetters.length || !zip,
  });

  return useMemo(() => {
    const map: CarrierNameMap = {};
    for (const p of data?.medigapPlans.data ?? []) {
      const naic = parseFloat(p.naic);
      if (!isNaN(naic)) {
        map[naic] = { company_name: p.orgName ?? '', parent_company: p.parentOrgName ?? null };
      }
    }
    return map;
  }, [data]);
}
